import React, { useEffect, useState } from 'react';
import { Button, Result, message } from 'antd';
import { discardPendingOrder, getRetryPayment } from 'utils/apis/eInvoice';
import { withAppContext } from 'context/AppContext';

const isAbandonedCheckoutStatus = status => status === 'cancelled' || status === 'failed';

const clearPaymentQueryParams = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete('status');
  url.searchParams.delete('orderId');
  window.history.replaceState({ path: url.toString() }, '', url.toString());
};

const EInvoicePaymentStatus = props => {
  const [loading, setLoading] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [retryError, setRetryError] = useState(null);

  const effectiveStatus = isAbandonedCheckoutStatus(props.paymentStatus) ? 'cancelled' : props.paymentStatus;

  useEffect(() => {
    if (isAbandonedCheckoutStatus(props.paymentStatus) && props.retryId) {
      getCheckoutRetryUrl(props.retryId);
    }
  }, []);

  const getCheckoutRetryUrl = async retryId => {
    setLoading(true);
    setRetryError(null);
    const res = await getRetryPayment(retryId);
    setLoading(false);

    if (res.status >= 200 && res.status < 300) {
      setPaymentData(res.data);
    } else {
      setRetryError((res && res.data && res.data.message) || 'Unable to resume checkout. Please start a new signup from the Companies page.');
    }
  };

  const handleContinueCheckout = () => {
    if (paymentData && paymentData.redirectUrl) {
      window.location.assign(paymentData.redirectUrl);
    }
  };

  const handleBackToEInvoice = async () => {
    if (isAbandonedCheckoutStatus(props.paymentStatus) && props.retryId) {
      setDiscarding(true);
      const res = await discardPendingOrder(props.retryId);
      setDiscarding(false);

      if (!(res && res.status >= 200 && res.status < 300)) {
        message.error((res && res.data && res.data.message) || 'Failed to clear pending signup.');
      }
    }

    clearPaymentQueryParams();
    if (props.onClearPaymentStatus) {
      props.onClearPaymentStatus();
    } else {
      window.location.href = '/e-invoice';
    }
  };

  const renderAbandonedCheckout = () => (
    <Result
      status="info"
      title="Checkout cancelled"
      subTitle="You left checkout before paying. Nothing was charged."
      extra={[
        <Button
          key="continue"
          size="large"
          type="primary"
          loading={loading}
          disabled={discarding || (!(paymentData && paymentData.redirectUrl) && !retryError)}
          onClick={handleContinueCheckout}
        >
          Continue checkout
        </Button>,
        props.onChangePlan && props.retryId ? (
          <Button key="change-plan" size="large" disabled={discarding} onClick={() => props.onChangePlan(props.retryId)}>
            Change plan
          </Button>
        ) : null,
        <Button key="back" size="large" loading={discarding} onClick={handleBackToEInvoice}>
          Back to e-Invoice
        </Button>
      ].filter(Boolean)}
    >
      {retryError ? <div style={{ color: '#cf1322' }}>{retryError}</div> : null}
    </Result>
  );

  const renderPaymentStatus = () => {
    switch (effectiveStatus) {
      case 'success':
        return (
          <Result
            status="success"
            title="Payment Successful"
            subTitle="Your payment has been successfully processed."
            extra={[
              <Button size="large" type="primary" onClick={handleBackToEInvoice}>
                Back to e-Invoice
              </Button>
            ]}
          />
        );

      case 'cancelled':
        return renderAbandonedCheckout();

      default:
        return null;
    }
  };

  return <div>{renderPaymentStatus()}</div>;
};

export default withAppContext(EInvoicePaymentStatus);
